import { useState, useEffect } from 'react';
import { Stack, Table, TableBody, TableContainer } from '@mui/material';
import {
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow
} from '@/components/shared/styles/Table';
import { TableCard } from '@/components/shared/Card';
import CardHeader from '@/components/shared/CardHeader';
import Chip from '@/components/shared/Chip';
import { PrimaryButton } from '@/components/shared/Button';
import { ButtonContainer } from '@/pages/company/PaymentSetting';
import { t } from 'i18next';
import { useGetEmployeeDocuments } from '@/services/paymentsQuery';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { DATE_FORMAT } from '@/lib/constants';
import { UploadNewDocumentDialog } from './UploadNewDocumentDialog';
import { Document } from '@/services/PaymentsServiceV2';
import { EditIcon } from '@/icons/EditIcon';
import { useFormatting } from '@/utils/formatting';

const getStatusColor = (status: any) => {
    switch (status) {
        case 'Active':
            return 'success' as const;
        case 'Inactive':
            return 'muted' as any;
        default:
            return 'warning' as const;
    }
};

export const Status = ({ status }: any) => {
    return <Chip color={getStatusColor(status)} label={status} />;
};

export const EmployeeSigningDocuments = () => {
    const { data, error } = useGetEmployeeDocuments();
    const { formatDate } = useFormatting();

    useHandleResponse({
        error
    });

    const [openCreateDocumentDialog, setOpenCreateDocumentDialog] =
        useState(false);
    const [documentToEdit, setDocumentToEdit] = useState<
        Document | undefined
    >();

    useEffect(() => {
        if (documentToEdit && data?.data?.documents) {
            const updatedDocument = data.data.documents.find(
                (doc) => doc.documentId === documentToEdit.documentId
            );
            if (updatedDocument) {
                setDocumentToEdit(updatedDocument);
            }
        }
    }, [data?.data?.documents, documentToEdit]);

    return (
        <>
            <TableCard style={{ marginTop: '24px' }}>
                <CardHeader
                    label={
                        <Stack direction={'row'} alignItems={'center'}>
                            {t('documents.employee_singing_documents')}
                        </Stack>
                    }
                ></CardHeader>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>
                                    {t('documents.upload_date_time')}
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    {t('documents.document_name')}
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    {t('documents.languages')}
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    {t('documents.sign_required')}
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    {t('documents.status')}
                                </TableHeaderCell>
                                <TableHeaderCell
                                    width={'30px'}
                                ></TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data?.data?.documents.map((item, _index) => {
                                return (
                                    <TableRow key={item.documentId}>
                                        <TableCell>
                                            {formatDate(
                                                item.createdAt,
                                                DATE_FORMAT.FULL_DATE_TIME
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {item?.files[0]?.name}
                                        </TableCell>
                                        <TableCell>
                                            {item.files
                                                .map((file) =>
                                                    t(
                                                        'languages.' +
                                                            file.language
                                                    )
                                                )
                                                .join(', ')}
                                        </TableCell>
                                        <TableCell align="left">
                                            {item.requiresReSign
                                                ? t('yes')
                                                : t('no')}
                                        </TableCell>
                                        <TableCell>
                                            <Status status={item.status} />
                                        </TableCell>
                                        <TableCell
                                            disabled={!item.isEditable}
                                            onClick={() => {
                                                if (item.isEditable) {
                                                    setDocumentToEdit(item);
                                                }
                                            }}
                                            sx={{
                                                cursor:
                                                    item.status !== 'Inactive'
                                                        ? 'pointer'
                                                        : undefined
                                            }}
                                        >
                                            <EditIcon size={16} />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <ButtonContainer style={{ borderTop: 'none' }}>
                    <PrimaryButton
                        onClick={() => {
                            setOpenCreateDocumentDialog(true);
                        }}
                        sx={{ marginLeft: '6px !important' }}
                        size="medium"
                    >
                        {t('upload_new_employee_signing_document')}
                    </PrimaryButton>
                </ButtonContainer>
            </TableCard>
            <UploadNewDocumentDialog
                open={openCreateDocumentDialog || documentToEdit !== undefined}
                onClose={() => {
                    setOpenCreateDocumentDialog(false);
                    setDocumentToEdit(undefined);
                }}
                document={documentToEdit}
            />
        </>
    );
};
